import { useEffect, useState } from "react";
import { Loader2, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

/**
 * iter-56 EditCharacterDialog.
 *
 * Inline, local edit for a single character. Used from the per-character
 * detail page so the pencil affordance never punts the user back to the
 * Cast overview. PATCHes only the fields that actually changed.
 *
 * After save the dialog closes and the parent receives the updated record
 * via `onSaved(updated)` so it can refresh its local state without
 * re-fetching everything.
 */
export default function EditCharacterDialog({
    open,
    onOpenChange,
    projectId,
    character,
    onSaved,
}) {
    const apiBase = useApiBase();
    const [name, setName] = useState("");
    const [actor, setActor] = useState("");
    const [number, setNumber] = useState("");
    const [notes, setNotes] = useState("");
    const [team, setTeam] = useState("main");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    // Iter-157C — calm confirmation step for script-derived character
    // renames. Manual characters rename in one click as before; only
    // parsed-from-script characters surface the explanatory pause.
    const [pendingRenamePatch, setPendingRenamePatch] = useState(null);

    // Sync from the incoming character whenever it changes.
    useEffect(() => {
        if (!character) return;
        setName(character.name || "");
        setActor(character.actor || "");
        setNumber(character.character_number || "");
        setNotes(character.notes || "");
        setTeam(character.team || "main");
        setError("");
        setPendingRenamePatch(null);
    }, [character, open]);

    const persistPatch = async (patch) => {
        setSaving(true);
        setError("");
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/characters/${character.id}`,
                patch,
            );
            onSaved?.(data);
            toast.success(`Saved ${data.name || "character"}`);
            onOpenChange(false);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
            setPendingRenamePatch(null);
        }
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!character) return;
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError("Name is required.");
            return;
        }
        // Build a minimal patch — send ONLY the fields the user changed
        // so the backend leaves untouched fields alone.
        const patch = {};
        if (trimmedName !== (character.name || "")) patch.name = trimmedName;
        if ((actor.trim() || null) !== (character.actor || null)) {
            patch.actor = actor.trim() || null;
        }
        if ((number.trim() || null) !== (character.character_number || null)) {
            patch.character_number = number.trim();
        }
        if ((notes.trim() || null) !== (character.notes || null)) {
            patch.notes = notes.trim() || null;
        }
        if (team !== (character.team || "main")) patch.team = team;
        if (Object.keys(patch).length === 0) {
            onOpenChange(false);
            return;
        }
        // Iter-157C — gate rename of script-derived characters behind
        // a calm explanatory step. Only fires when the name itself is
        // changing AND the character originated from the script.
        if (patch.name && character.is_script_derived) {
            setPendingRenamePatch(patch);
            return;
        }
        await persistPatch(patch);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !saving && !pendingRenamePatch && onOpenChange(v)}>
            {/* Iter-158.13.2.20f.17 — real-device iOS Safari scroll fix.
                Supersedes .20f.13 diagnosis (`100svh` alone was
                insufficient). Real-device UAT showed the DIALOG did
                not scroll on iPhone Safari; instead the underlying
                PAGE became the scroll target and Save/Cancel sat
                below the visible edge. Root cause: our pre-.20f.11
                baseline set `position: relative` on DialogContent,
                which OVERRIDES Radix's default `position: fixed`.
                A non-fixed DialogContent contributes to document
                flow height, so on iOS Safari the body-scroll-lock
                Radix applies (`overflow:hidden` on <body>) is
                defeated — touch swipes route to the page, never to
                the inner form's `overflow-y-auto`. Also `top:50%`
                on a `relative` element anchors to layout viewport
                (unaware of URL-bar / keyboard chrome), so vertical
                centring drifts below the visual viewport.
                Repair: drop `relative` so Radix's `fixed` applies.
                The pre-existing script-rename overlay uses
                `absolute inset-0` and works fine against a
                fixed-positioned ancestor (fixed IS positioned for
                absolute containment). Keep `100svh` bounded height
                and the inner scrollable form. Add `pb-[max(1rem,env(safe-area-inset-bottom))]`
                and `touch-action:pan-y` to the form so on iPhones
                with a home indicator, Save sits above the safe area
                and iOS reliably routes vertical scroll gestures to
                the form. */}
            <DialogContent
                data-testid="edit-character-dialog"
                className="max-w-md bg-card border-white/10 overflow-hidden max-h-[calc(100svh_-_2rem)] flex flex-col"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white">
                        Edit character
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Updates apply everywhere this character appears —
                        every scene attachment and existing image keeps its
                        link. Continuity data is never touched.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-4 overflow-y-auto flex-1 min-h-0 pr-1 -mr-1 overscroll-contain touch-pan-y pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <div className="grid grid-cols-[80px_1fr] gap-3">
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="edit-character-number"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                #
                            </Label>
                            <Input
                                id="edit-character-number"
                                data-testid="edit-character-number"
                                value={number}
                                onChange={(e) => setNumber(e.target.value)}
                                placeholder="—"
                                inputMode="numeric"
                                maxLength={6}
                                className="h-12 bg-secondary border-transparent text-center font-mono"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="edit-character-name"
                                className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Name
                            </Label>
                            <Input
                                id="edit-character-name"
                                data-testid="edit-character-name"
                                autoFocus
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (error) setError("");
                                }}
                                placeholder="Character name"
                                maxLength={120}
                                className="h-12 bg-secondary border-transparent"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <Label
                            htmlFor="edit-character-actor"
                            className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Actor / casting
                        </Label>
                        <Input
                            id="edit-character-actor"
                            data-testid="edit-character-actor"
                            value={actor}
                            onChange={(e) => setActor(e.target.value)}
                            placeholder="Optional"
                            maxLength={120}
                            className="h-12 bg-secondary border-transparent"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label
                            htmlFor="edit-character-notes"
                            className="text-xs uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Description &amp; notes
                        </Label>
                        <Textarea
                            id="edit-character-notes"
                            data-testid="edit-character-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Wardrobe, hair, distinguishing details…"
                            maxLength={2000}
                            rows={4}
                            className="bg-secondary border-transparent leading-snug"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                            Team
                        </Label>
                        <div className="flex items-center rounded-md bg-secondary border border-white/10 overflow-hidden">
                            {[
                                { id: "main", label: "Main team" },
                                { id: "crowd", label: "Crowd" },
                            ].map((t, i) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    data-testid={`edit-character-team-${t.id}`}
                                    onClick={() => setTeam(t.id)}
                                    className={[
                                        "flex-1 h-11 text-xs font-semibold uppercase tracking-wider transition-colors",
                                        team === t.id
                                            ? "bg-primary text-primary-foreground"
                                            : "text-neutral-300 hover:bg-white/10",
                                        i > 0 ? "border-l border-white/10" : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {error ? (
                        <p
                            data-testid="edit-character-error"
                            className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 text-red-200 text-sm px-3 py-2"
                        >
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </p>
                    ) : null}

                    <div className="flex gap-2 pt-1">
                        <Button
                            type="button"
                            data-testid="edit-character-cancel"
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="edit-character-save"
                            disabled={saving || !name.trim()}
                            className="flex-1 min-tap font-semibold"
                        >
                            {saving ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <>
                                    <Save size={14} className="mr-1.5" />
                                    Save
                                </>
                            )}
                        </Button>
                    </div>
                </form>
                {/* Iter-157C — script-derived rename confirmation.
                    Calm, explanatory, non-blocking outside this case. */}
                {pendingRenamePatch ? (
                    <div
                        data-testid="edit-character-rename-confirm"
                        className="absolute inset-0 bg-background/95 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center p-6 text-center gap-4 z-10"
                    >
                        <AlertTriangle size={28} className="text-amber-300" />
                        <div>
                            <h3 className="text-base font-semibold text-white">
                                Rename a script character?
                            </h3>
                            <p className="mt-2 text-sm text-neutral-300 max-w-sm leading-relaxed">
                                This character was created from the script.
                                Renaming it may affect continuity tracking and
                                future script revisions.
                            </p>
                            <p className="mt-3 text-[12px] text-neutral-400 leading-relaxed">
                                <span className="line-through text-neutral-500">
                                    {character?.name || "—"}
                                </span>{" "}
                                →{" "}
                                <span className="font-semibold text-amber-200">
                                    {pendingRenamePatch.name}
                                </span>
                            </p>
                        </div>
                        <div className="flex items-center gap-3 w-full">
                            <Button
                                data-testid="edit-character-rename-cancel"
                                type="button"
                                variant="secondary"
                                onClick={() => setPendingRenamePatch(null)}
                                className="flex-1 min-tap h-12"
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="edit-character-rename-confirm-ok"
                                type="button"
                                onClick={() => persistPatch(pendingRenamePatch)}
                                disabled={saving}
                                className="flex-1 min-tap h-12 bg-amber-400 hover:bg-amber-400/90 text-black font-semibold"
                            >
                                {saving ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : "Confirm rename"}
                            </Button>
                        </div>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
